/**
 * Storefront analytics collection (Design §12) — the whole of the client half.
 *
 * **Nothing in this file may ever break a page.** Every public function swallows its own errors,
 * every storage access is guarded, and a flush that fails is dropped rather than retried. A store
 * that will not render because a reporting call threw is a worse outcome than every number on the
 * dashboard being wrong, and the two failures are not close in cost.
 *
 * The identity here is three things and they are not the same thing (§12.1):
 *
 * - **`visitorUUID`** lives in `localStorage` and is a browser profile. It is what makes "looked in
 *   March, came back in May and bought" one story rather than two strangers.
 * - **`sessionUUID`** lives in `sessionStorage` *and* carries an idle deadline in `localStorage`,
 *   so a second tab continues the visit rather than starting a second one — `sessionStorage` is
 *   per-tab, and a customer who opens the comparison table in a new tab has not begun a new visit.
 * - **the account** is never sent from here at all. The server reads it from the verified token, so
 *   a client claiming to be somebody is recorded as anonymous.
 *
 * It is **cookie-less**: first-party browser storage, scoped to this origin, read by nobody else.
 */

import site from '../site';
import { configurationStore } from '../AppConfiguration';

/** Where identity lives. Prefixed, because `localStorage` is shared with everything else on the origin. */
const VISITOR_KEY = 'abstractstore.analytics.visitor';
const SESSION_KEY = 'abstractstore.analytics.session';
const DEADLINE_KEY = 'abstractstore.analytics.deadline';
const ATTRIBUTION_KEY = 'abstractstore.analytics.attribution';

/**
 * The client's idea of when a visit ends.
 *
 * A copy of `analytics.sessionIdleMinutes`, and deliberately not fetched: the server is the
 * authority on what a session *means* for a report, and this is only the client's decision about
 * when to mint a new identifier. The two disagreeing costs one session boundary in the wrong place,
 * where fetching a setting before the first event could cost the first event entirely.
 */
const IDLE_MS = 30 * 60 * 1000;

/** How long a batch waits for company before it goes. Long enough to batch, short enough to survive a tab close. */
const FLUSH_DELAY_MS = 4000;

/** Matches the server's `analytics.maxEventsPerBatch` default; the server refuses beyond its own. */
const MAX_BATCH = 50;

/**
 * Every kind of thing the storefront reports (§12.2).
 *
 * A union here and a CHECK constraint there. A type this file can emit and the server does not know
 * is dropped silently, which is why the two lists are worth keeping in one shape — and why
 * `AnalyticsEventTypeAgreement.test.ts` reads the server's own file rather than trusting this one.
 */
export type AnalyticsEventType =
	| 'page_view'
	| 'catalog_view'
	| 'family_view'
	| 'product_view'
	| 'plan_selected'
	| 'currency_changed'
	| 'seats_changed'
	| 'option_toggled'
	| 'coupon_attempted'
	| 'checkout_step'
	| 'signin_started'
	| 'signin_completed'
	| 'checkout_created'
	| 'checkout_returned'
	| 'checkout_canceled'
	| 'change_started'
	| 'change_committed'
	| 'error_shown';

export interface TrackContext {
	path?: string;
	productSlug?: string;
	familySlug?: string;
	priceUUID?: string;
	currency?: string;
	properties?: Record<string, unknown>;
}

interface QueuedEvent extends TrackContext {
	type: AnalyticsEventType;
	/** When it happened, in wall-clock terms, converted to an offset at flush time (§12.3). */
	at: number;
}

interface StoredAttribution {
	landingPath?: string;
	referrer?: string;
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmTerm?: string;
	utmContent?: string;
}

/**
 * The page's own context, merged into every event raised while that page is open.
 *
 * **Most events know less about themselves than the page does.** A `checkout_step` is raised by
 * `CheckoutSteps`, which is a shared component with no idea which product it is selling; a
 * `signin_completed` knows nothing at all. Left to the call sites, every one of those lands in the
 * dashboard with a null product, a null currency and a null path — and a breakdown that groups them
 * anyway reports the lot as "Unknown", which is what most of the Pages, Products, Product line and
 * Currency reports consisted of. The page always knew; nothing was carrying it.
 *
 * It is **scoped to a path and dropped when the path changes**, rather than cleared by a lifecycle
 * hook. A caller that forgot to clear would attribute the catalogue's events to the last product
 * viewed, which is worse than the gap it replaced — and route changes are exactly when that would
 * happen, since the router destroys and mounts in an order no call site should have to know.
 */
let ambient: TrackContext = {};
let ambientPath = '';

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let enabled = true;
let started = false;

/**
 * `analytics.enabled`, mirrored from `/api/configuration`.
 *
 * The switch has to govern **both ends** (§12.5). With only the server half, a page already open
 * keeps sending batches that are silently dropped; with only the client half, a stale bundle keeps
 * writing rows after the operator turned collection off. Neither failure surfaces anywhere, which
 * is why it is worth two lines of code.
 */
configurationStore.subscribe(configuration => {
	if (configuration !== null) {
		enabled = configuration.analyticsEnabled !== false;
		if (!enabled) {
			queue = [];
		}
	}
});

function readStorage(storage: Storage | undefined, key: string): string | null {
	try {
		return storage?.getItem(key) ?? null;
	} catch {
		// A private window, a browser set to block site data, or a sandboxed frame. Analytics is
		// not a reason to fail, so an unreadable store simply means no identity this visit.
		return null;
	}
}

function writeStorage(storage: Storage | undefined, key: string, value: string): void {
	try {
		storage?.setItem(key, value);
	} catch {
		/* See `readStorage`. */
	}
}

/**
 * A random identifier.
 *
 * `crypto.randomUUID` where it exists, and a `getRandomValues` fallback where it does not — it is
 * absent on any non-secure origin, which is every local development server that is not
 * `localhost`. A fallback matters because the server validates the shape: a non-UUID identity makes
 * the whole batch a silent no-op.
 */
export function newUUID(): string {
	const cryptoAPI = globalThis.crypto as Crypto | undefined;
	if (cryptoAPI !== undefined && typeof cryptoAPI.randomUUID === 'function') {
		return cryptoAPI.randomUUID();
	}
	const bytes = new Uint8Array(16);
	if (cryptoAPI !== undefined && typeof cryptoAPI.getRandomValues === 'function') {
		cryptoAPI.getRandomValues(bytes);
	} else {
		for (let index = 0; index < bytes.length; index += 1) {
			bytes[index] = Math.floor(Math.random() * 256);
		}
	}
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function visitorID(): string {
	const existing = readStorage(globalThis.localStorage, VISITOR_KEY);
	if (existing !== null && existing.length > 0) return existing;
	const minted = newUUID();
	writeStorage(globalThis.localStorage, VISITOR_KEY, minted);
	return minted;
}

/**
 * The current visit, minting a new one once the idle deadline has passed.
 *
 * The deadline is in `localStorage` rather than beside the session id in `sessionStorage`, and that
 * is the whole trick: `sessionStorage` is per-tab, so a customer who opens the comparison table in
 * a second tab would otherwise start a second visit — and a funnel counting one person twice
 * reports half the conversion rate it should.
 */
export function sessionID(now: number = Date.now()): string {
	const deadline = Number.parseInt(readStorage(globalThis.localStorage, DEADLINE_KEY) ?? '0', 10);
	const existing = readStorage(globalThis.sessionStorage, SESSION_KEY)
		?? readStorage(globalThis.localStorage, SESSION_KEY);
	const live = existing !== null && existing.length > 0 && Number.isFinite(deadline) && now < deadline;
	const identifier = live ? (existing as string) : newUUID();
	writeStorage(globalThis.sessionStorage, SESSION_KEY, identifier);
	writeStorage(globalThis.localStorage, SESSION_KEY, identifier);
	writeStorage(globalThis.localStorage, DEADLINE_KEY, String(now + IDLE_MS));
	return identifier;
}

/**
 * The visitor's attribution, captured **once** and kept.
 *
 * Captured at landing rather than read per event, because by the time somebody buys, the referrer
 * is this store and the UTM parameters are long gone from the URL — the campaign that brought them
 * is only knowable at the moment they arrive.
 *
 * The referrer is sent whole and reduced to its host **server-side**: a full referrer routinely
 * carries another site's search terms and session identifiers, and the trimming belongs where the
 * rule can be relied upon rather than in a bundle a visitor can edit.
 */
/**
 * The attribution for **this visit**, captured once at its entry and held for its duration.
 *
 * Two touches, and they are different questions (§12.4). The **visitor** keeps first touch — which
 * campaign introduced this person — and the **session** keeps last touch: what they were doing
 * immediately before this particular visit. Both are written insert-only server-side, so all this
 * has to do is report the truth about the visit it is in.
 *
 * **Keyed to the session, not cached for ever.** Holding one block in `localStorage` permanently is
 * the bug this replaced: every visit after the first re-sent the original campaign, so last touch
 * was never recorded and the acquisition report was first touch wearing the wrong label — which
 * under-credits precisely the campaigns aimed at people who already know the store. Keying it to
 * the visit keeps it stable while the visit lasts (the entry page and referrer must survive the
 * customer navigating on before the first batch flushes) and lets the next visit tell its own story.
 */
export function captureAttribution(
	url: string,
	referrer: string,
	sessionUUID: string
): StoredAttribution {
	const stored = readStorage(globalThis.localStorage, ATTRIBUTION_KEY);
	if (stored !== null) {
		try {
			const held = JSON.parse(stored) as { sessionUUID?: string; attribution?: StoredAttribution };
			if (held.sessionUUID === sessionUUID && held.attribution !== undefined) {
				return held.attribution;
			}
		} catch {
			/* A corrupt blob is replaced rather than trusted. */
		}
	}
	let parameters: URLSearchParams;
	let path = '/';
	try {
		const parsed = new URL(url);
		parameters = parsed.searchParams;
		path = parsed.pathname;
	} catch {
		parameters = new URLSearchParams();
	}
	const attribution: StoredAttribution = {
		landingPath: path,
		// An empty referrer is a direct visit, which is a fact rather than a missing value — but it
		// is expressed by the field's absence, since the column means "the host that sent them".
		...(referrer.length > 0 && !isOwnOrigin(referrer) ? { referrer } : {}),
		...textParameter(parameters, 'utm_source', 'utmSource'),
		...textParameter(parameters, 'utm_medium', 'utmMedium'),
		...textParameter(parameters, 'utm_campaign', 'utmCampaign'),
		...textParameter(parameters, 'utm_term', 'utmTerm'),
		...textParameter(parameters, 'utm_content', 'utmContent')
	};
	writeStorage(globalThis.localStorage, ATTRIBUTION_KEY, JSON.stringify({ sessionUUID, attribution }));
	return attribution;
}

/**
 * Whether a referrer is the store itself.
 *
 * A visit that resumes after its idle deadline does so on a page of ours, so the referrer at that
 * moment is our own origin — recording it would invent a referring site and put the store at the
 * top of its own acquisition report. Its absence is the correct answer: they were already here.
 */
function isOwnOrigin(referrer: string): boolean {
	try {
		return new URL(referrer).origin === globalThis.location?.origin;
	} catch {
		return false;
	}
}

function textParameter(
	parameters: URLSearchParams,
	name: string,
	key: string
): Record<string, string> {
	const value = (parameters.get(name) ?? '').trim();
	return value.length === 0 ? {} : { [key]: value.slice(0, 255) };
}

/**
 * Record one thing.
 *
 * Queued rather than sent: a storefront produces several events in the same second — a page view, a
 * product view and a plan selection all land while one page renders — and a request each would be
 * three round trips for one screen.
 */
/** The path an event is happening on, read at the moment it happens rather than at flush. */
function currentPath(): string {
	try {
		return globalThis.location?.pathname ?? '';
	} catch {
		return '';
	}
}

/**
 * What the page knows about itself, for the events raised on it that do not.
 *
 * Called by a page as its subject settles — the product, its line, the currency on the rail — and
 * never cleared by hand: it lapses the moment the path changes. An explicit value on a `track` call
 * still wins, so a component that does know better is not overridden by the page.
 */
export function setAnalyticsContext(context: TrackContext): void {
	ambient = context;
	ambientPath = currentPath();
}

/** Test seam, and the one case a page legitimately drops its own context early. */
export function clearAnalyticsContext(): void {
	ambient = {};
	ambientPath = '';
}

export function track(type: AnalyticsEventType, context: TrackContext = {}): void {
	try {
		if (!enabled) return;
		const path = currentPath();
		queue.push({
			type,
			at: Date.now(),
			// Every event happens on a page, so every event carries one. The ambient block applies
			// only while the path it was set on is still the path in the address bar.
			...(path.length > 0 ? { path } : {}),
			...(path === ambientPath ? ambient : {}),
			...context
		});
		if (queue.length >= MAX_BATCH) {
			void flush();
			return;
		}
		if (timer === null) {
			timer = setTimeout(() => {
				timer = null;
				void flush();
			}, FLUSH_DELAY_MS);
		}
	} catch {
		/* See the file comment: analytics may not break a page. */
	}
}

/**
 * A page view, and the one emitter that decides for itself whether to fire.
 *
 * `/Settings/*` reports nothing at all (§12.2): administration is not shopping, and on a young
 * store an operator clicking through their own catalogue would be the largest source of product
 * views there is — inflating the denominator of every conversion rate on the page they are reading
 * it from. The server's `analytics.excludeAdmins` is the other half, for an admin browsing the
 * storefront proper.
 */
export function trackPageView(path: string): void {
	if (path.startsWith('/Settings')) return;
	track('page_view', { path });
}

/**
 * Send what is queued.
 *
 * A **keepalive `fetch`** even on the way out of the page, because the most interesting event in a
 * funnel is routinely the last one before somebody left — and a plain `fetch` issued during
 * `pagehide` is cancelled by the navigation that caused it. `keepalive` is what survives it, and it
 * carries headers, so the batch keeps its token. `sendBeacon` is the fallback for a browser that has
 * no keepalive: it survives the navigation too but **can set no headers at all**, so anything sent
 * that way is necessarily anonymous.
 */
export async function flush(useBeacon: boolean = false): Promise<void> {
	try {
		if (timer !== null) {
			clearTimeout(timer);
			timer = null;
		}
		if (!enabled || queue.length === 0) return;
		const sending = queue.slice(0, MAX_BATCH);
		queue = queue.slice(MAX_BATCH);
		const now = Date.now();
		const body = JSON.stringify({
			visitorUUID: visitorID(),
			sessionUUID: sessionID(now),
			attribution: captureAttribution(
				globalThis.location?.href ?? '/',
				globalThis.document?.referrer ?? '',
				sessionID(now)
			),
			events: sending.map(event => ({
				type: event.type,
				// A relative offset, never a timestamp. A client clock wrong by years would
				// otherwise put rows outside every range a report will ever draw, while still
				// counting toward its totals (§12.3).
				offsetMs: Math.max(now - event.at, 0),
				path: event.path,
				productSlug: event.productSlug,
				familySlug: event.familySlug,
				priceUUID: event.priceUUID,
				currency: event.currency,
				properties: event.properties
			}))
		});
		const url = `${site.backend.baseURL}${site.backend.api.analyticsCollect}`;
		const headers = withToken({ 'Content-Type': 'application/json' });

		/*
		 * `keepalive` first, **including on the way out of the page**, and `sendBeacon` only where it
		 * does not exist.
		 *
		 * A keepalive fetch survives the navigation exactly as a beacon does — that is what the flag
		 * is for — and unlike a beacon it can carry headers. **`sendBeacon` cannot set any**, so a
		 * batch sent that way arrives with no token and is recorded as anonymous. That was written
		 * off as acceptable for "the last batch of a visit" and is not: `visibilitychange` fires
		 * every time the customer switches tab, so in practice most batches leave this way, and each
		 * one silently drops the identity. The store had 33 events, none carrying a user, and eight
		 * of them were page views of `/Account/*` — pages that cannot be reached without signing in.
		 *
		 * What that costs is not one column: the §12.1 stitch never fires, so no anonymous journey is
		 * ever joined to the account that completed it; the signed-in filter reports everybody as
		 * anonymous; and `analytics.excludeAdmins` cannot exclude an administrator it is never told
		 * about, so operator traffic lands in the funnel.
		 *
		 * The beacon stays as the fallback rather than being deleted, because a browser without
		 * keepalive would otherwise lose the last batch of every visit — which is the event a funnel
		 * most wants.
		 */
		const keepaliveSupported = typeof Request === 'function' && 'keepalive' in Request.prototype;
		if (useBeacon && !keepaliveSupported) {
			if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
				navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
			}
			return;
		}
		await fetch(url, { method: 'POST', headers, body, keepalive: true });
	} catch {
		/* Dropped, never retried: a retry queue in a browser tab is a backlog that only grows. */
	}
}

/**
 * The token, read from where `AbstractID` keeps it, without importing it.
 *
 * A direct import would make this module depend on the auth module and the auth module's
 * initialisation order, for one header — and this file has to be safe to call from anywhere,
 * including before anything has booted.
 */
function withToken(headers: Record<string, string>): Record<string, string> {
	try {
		const token = globalThis.localStorage?.getItem('jwt2');
		return token === null || token === undefined || token.length === 0
			? headers
			: { ...headers, 'X-AbstractStore-UserToken': token };
	} catch {
		return headers;
	}
}

/**
 * Mount the page-lifecycle hooks. Called once, from `App.svelte`.
 *
 * `pagehide` rather than `unload`: `unload` is ignored by every mobile browser and disables the
 * back-forward cache in the ones that honour it, so a tracker built on it loses exactly the mobile
 * traffic it was added to measure. `visibilitychange` catches a tab switched away from and never
 * returned to, which on a phone is how most visits actually end.
 */
export function startAnalytics(): void {
	if (started || typeof window === 'undefined') return;
	started = true;
	// At boot, which is the visit's true entry: the URL and referrer here are the ones that brought
	// them, and by the first flush the customer may already have navigated on.
	captureAttribution(
		globalThis.location?.href ?? '/',
		globalThis.document?.referrer ?? '',
		sessionID()
	);
	window.addEventListener('pagehide', () => void flush(true));
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') void flush(true);
	});
}

/** Test seam: forget everything queued and every listener's effect. Never called by the app. */
export function resetAnalyticsForTest(): void {
	queue = [];
	started = false;
	enabled = true;
	clearAnalyticsContext();
	if (timer !== null) {
		clearTimeout(timer);
		timer = null;
	}
}
